import {
  Box,
  Card,
  TextField,
  MenuItem,
  FormControl,
  Select,
  CircularProgress,
  FormControlLabel,
  Switch,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useEffect, useState } from "react";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { useLocation, useNavigate } from "react-router-dom";
import api from "api/api";

const fieldSx = {
  "& .MuiInputBase-root": {
    display: "flex !important",
    placeItems: "unset !important",
    alignItems: "center !important",
    justifyContent: "flex-start !important",
    width: "100% !important",
  },
  "& .MuiInputBase-input": {
    width: "100% !important",
    textAlign: "left !important",
  },
  "& .MuiInputBase-inputMultiline": {
    width: "100% !important",
    textAlign: "left !important",
    display: "block !important",
  },
  "& textarea": {
    width: "100% !important",
    textAlign: "left !important",
  },
};

const selectSx = {
  "& .MuiInputBase-root": {
    display: "flex !important",
    placeItems: "unset !important",
    alignItems: "center !important",
    justifyContent: "flex-start !important",
    width: "100% !important",
  },
  "& .MuiSelect-select": {
    textAlign: "left !important",
    width: "100% !important",
  },
};

const iconButtonSx = {
  backgroundColor: "info.main",
  color: "#ffffff",
  borderRadius: "50%",
  width: "32px",
  height: "32px",
  flexShrink: 0,
  "&:hover": {
    backgroundColor: "info.main",
    opacity: 0.9,
  },
};

const removeIconButtonSx = {
  ...iconButtonSx,
  backgroundColor: "error.main",
  "&:hover": {
    backgroundColor: "error.main",
    opacity: 0.9,
  },
};

const categoryOptions = [
  { value: "level", label: "Level" },
  { value: "badge", label: "Badge" },
  { value: "leaderboard", label: "Leaderboard" },
];

const issuanceModeOptions = [
  { value: "manual", label: "Manual (admin issues to user)" },
  { value: "auto", label: "Auto (system issues on trigger)" },
];

const rarityTierOptions = ["common", "rare", "elite"];

const defaultFormData = {
  name: "",
  key: "",
  description: "",
  category: "",
  issuance_mode: "manual",
  is_enabled: true,
  trigger_level: "",
  badge_trigger_type: "badge_key",
  trigger_badge_key: "",
  selected_badge_id: "",
  trigger_rarity_tier: "elite",
  trigger_placement: "",
  expiry_days: "90",
  benefits: [""],
};

const generateKey = (name) =>
  name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s]/g, "")
    .replace(/\s+/g, "_")
    .replace(/^_+|_+$/g, "");

const parseTriggerLogicToForm = (triggerLogic = {}) => {
  const next = {};

  if (triggerLogic.level != null) {
    next.trigger_level = String(triggerLogic.level);
  }
  if (triggerLogic.badge_key || triggerLogic.badge_id) {
    next.badge_trigger_type = "badge_key";
    next.trigger_badge_key = triggerLogic.badge_key || "";
    next.selected_badge_id = triggerLogic.badge_id ? String(triggerLogic.badge_id) : "";
  } else if (triggerLogic.rarity_tier) {
    next.badge_trigger_type = "rarity_tier";
    next.trigger_rarity_tier = triggerLogic.rarity_tier;
  }
  if (triggerLogic.placement != null) {
    next.trigger_placement = String(triggerLogic.placement);
  }
  if (triggerLogic.expiry_days != null) {
    next.expiry_days = String(triggerLogic.expiry_days);
  }
  if (Array.isArray(triggerLogic.benefits) && triggerLogic.benefits.length) {
    next.benefits = triggerLogic.benefits;
  }

  return next;
};

const buildTriggerLogic = (formData) => {
  if (formData.issuance_mode !== "auto") return undefined;

  const logic = {};
  const benefits = (formData.benefits || []).map((item) => item.trim()).filter(Boolean);

  if (benefits.length) logic.benefits = benefits;
  if (formData.expiry_days) logic.expiry_days = Number(formData.expiry_days);

  switch (formData.category) {
    case "level":
      logic.level = Number(formData.trigger_level);
      break;
    case "badge":
      if (formData.badge_trigger_type === "badge_key") {
        if (formData.selected_badge_id) {
          logic.badge_id = formData.selected_badge_id;
        }
        if (formData.trigger_badge_key.trim()) {
          logic.badge_key = formData.trigger_badge_key.trim();
        }
      } else {
        logic.rarity_tier = formData.trigger_rarity_tier;
      }
      break;
    case "leaderboard":
      logic.placement = Number(formData.trigger_placement);
      break;
    default:
      break;
  }

  return logic;
};

const validateAutoTrigger = (formData) => {
  if (formData.issuance_mode !== "auto") return null;

  if (!formData.category) {
    return "Category is required for auto rewards";
  }

  switch (formData.category) {
    case "level":
      if (!formData.trigger_level || Number(formData.trigger_level) <= 0) {
        return "Enter a valid target level for auto issue";
      }
      break;
    case "badge":
      if (formData.badge_trigger_type === "badge_key" && !formData.selected_badge_id) {
        return "Select a badge for auto issue";
      }
      break;
    case "leaderboard":
      if (!formData.trigger_placement || Number(formData.trigger_placement) <= 0) {
        return "Enter a valid leaderboard placement (e.g. 1 for first place)";
      }
      break;
    default:
      return "Select a valid category for auto rewards";
  }

  const benefits = (formData.benefits || []).map((item) => item.trim()).filter(Boolean);
  if (!benefits.length && !formData.description.trim()) {
    return "Add at least one auto benefit or a description";
  }

  return null;
};

const flattenBadgeDefinitions = (responseBody) => {
  const payload = responseBody?.data ?? responseBody ?? {};
  const badges = payload?.badges;
  if (!Array.isArray(badges)) return [];
  if (badges.length > 0 && Array.isArray(badges[0]?.badges)) {
    return badges.flatMap((group) => group.badges || []);
  }
  return badges;
};

const resolveBadgeKey = (badge) => {
  if (badge?.key) return badge.key;
  if (badge?.name) return generateKey(badge.name);
  return "";
};

const formatBadgeLabel = (badge) => {
  const parts = [badge?.name || "Unnamed badge"];
  const badgeKey = resolveBadgeKey(badge);
  if (badgeKey) parts.push(`(${badgeKey})`);
  if (badge?.category) parts.push(`· ${badge.category.replace(/_/g, " ")}`);
  if (badge?.rarity_tier) parts.push(`— ${badge.rarity_tier}`);
  return parts.join(" ");
};

export default function RewardForm() {
  const navigate = useNavigate();
  const location = useLocation();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [badgeOptions, setBadgeOptions] = useState([]);
  const [badgesLoading, setBadgesLoading] = useState(false);
  const [formData, setFormData] = useState(defaultFormData);

  const queryParams = new URLSearchParams(location.search);
  const rewardId = queryParams.get("rewardId");
  const isEditMode = Boolean(rewardId);
  const isAutoMode = formData.issuance_mode === "auto";

  const fetchBadgeOptions = async () => {
    try {
      setBadgesLoading(true);
      const res = await api.getBadgeDefinitions({
        groupByCategory: true,
        limit: 500,
        page: 1,
      });

      if (res.status === 1) {
        const badges = flattenBadgeDefinitions(res);
        setBadgeOptions(badges);

        setFormData((prev) => {
          const alreadySelected = badges.find(
            (badge) => String(badge._id) === String(prev.selected_badge_id)
          );
          if (alreadySelected) return prev;

          const matchedByKey = badges.find((badge) => {
            const badgeKey = resolveBadgeKey(badge);
            return badgeKey && badgeKey === prev.trigger_badge_key;
          });
          if (!matchedByKey) return prev;

          return {
            ...prev,
            selected_badge_id: matchedByKey._id,
            trigger_badge_key: resolveBadgeKey(matchedByKey),
          };
        });
      } else {
        setBadgeOptions([]);
      }
    } catch (err) {
      console.error(err);
      setBadgeOptions([]);
    } finally {
      setBadgesLoading(false);
    }
  };

  const getRewardDetails = async () => {
    if (!rewardId) return;

    try {
      setIsLoading(true);
      const res = await api.getRewards();

      if (res.status === 1) {
        const raw = res.data?.rewards ?? res.data ?? [];
        const rewards = Array.isArray(raw) ? raw : [];
        const reward = rewards.find((item) => item._id === rewardId);

        if (reward) {
          setFormData({
            ...defaultFormData,
            name: reward?.name || "",
            key: reward?.key || "",
            description: reward?.description || "",
            category: reward?.category || "",
            issuance_mode: reward?.issuance_mode || "manual",
            is_enabled: reward?.is_enabled !== false,
            ...parseTriggerLogicToForm(reward?.trigger_logic || {}),
          });
        } else {
          alert("Reward not found");
          navigate("/rewards");
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loginData = JSON.parse(
      localStorage.getItem("loginData") || sessionStorage.getItem("loginData")
    );

    if (!loginData) {
      navigate("/authentication/sign-in");
    } else if (rewardId) {
      getRewardDetails();
    }
  }, [rewardId]);

  useEffect(() => {
    if (formData.category === "badge" && isAutoMode) {
      fetchBadgeOptions();
    }
  }, [formData.category, isAutoMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      if (name === "name") {
        return {
          ...prev,
          name: value,
          key: generateKey(value),
        };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleBadgeSelect = (badgeId) => {
    const selectedBadge = badgeOptions.find((badge) => String(badge._id) === String(badgeId));
    setFormData((prev) => ({
      ...prev,
      selected_badge_id: badgeId,
      trigger_badge_key: resolveBadgeKey(selectedBadge),
    }));
  };

  const handleEnabledChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      is_enabled: e.target.checked,
    }));
  };

  const handleBenefitChange = (index, value) => {
    setFormData((prev) => {
      const updatedBenefits = [...prev.benefits];
      updatedBenefits[index] = value;
      return { ...prev, benefits: updatedBenefits };
    });
  };

  const handleAddBenefit = () => {
    setFormData((prev) => ({
      ...prev,
      benefits: [...prev.benefits, ""],
    }));
  };

  const handleRemoveBenefit = (index) => {
    setFormData((prev) => ({
      ...prev,
      benefits: prev.benefits.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.key || !formData.description || !formData.category) {
      alert("Please fill in all required fields");
      return;
    }

    const autoValidationError = validateAutoTrigger(formData);
    if (autoValidationError) {
      alert(autoValidationError);
      return;
    }

    try {
      setIsSubmitting(true);

      const payload = {
        key: formData.key,
        name: formData.name,
        description: formData.description,
        category: formData.category,
        issuance_mode: formData.issuance_mode,
      };

      const triggerLogic = buildTriggerLogic(formData);
      if (formData.issuance_mode === "auto" && triggerLogic) {
        payload.trigger_logic = triggerLogic;
      } else if (isEditMode) {
        payload.trigger_logic = null;
      }

      let res;

      if (isEditMode) {
        res = await api.updateReward({
          rewardId,
          ...payload,
          is_enabled: formData.is_enabled,
        });
      } else {
        res = await api.createReward(payload);
      }

      if (res.status === 1) {
        navigate("/rewards");
      } else {
        alert(res.message);
      }
    } catch (err) {
      console.error(err);
      alert("Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <SoftBox display="flex" justifyContent="center" mt={5}>
        <CircularProgress />
      </SoftBox>
    );
  }

  return (
    <SoftBox py={3} display="flex" justifyContent="center">
      <SoftBox width="100%" maxWidth="850px">
        <Card sx={{ border: "1px solid #f0f2f5" }}>
          <SoftBox p={3}>
            <SoftBox mb={4}>
              <SoftTypography variant="h6" fontWeight="bold">
                {isEditMode ? "Edit Reward" : "Create Reward"}
              </SoftTypography>
              <SoftTypography variant="caption" color="text">
                Define reward details, issuance mode, and auto-trigger rules.
              </SoftTypography>
            </SoftBox>

            <form onSubmit={handleSubmit}>
              <Box mb={3}>
                <SoftTypography variant="button" fontWeight="medium" mb={1}>
                  Name
                </SoftTypography>
                <TextField
                  fullWidth
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g. Level 10 Perk"
                  sx={fieldSx}
                />
              </Box>

              <Box mb={3}>
                <SoftTypography variant="button" fontWeight="medium" mb={1}>
                  Description
                </SoftTypography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  name="description"
                  placeholder="Describe what the user receives"
                  value={formData.description}
                  onChange={handleChange}
                  sx={fieldSx}
                />
              </Box>

              <Box mb={3}>
                <SoftTypography variant="button" fontWeight="medium" mb={1}>
                  Category
                </SoftTypography>
                <FormControl fullWidth sx={selectSx}>
                  <Select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Select category
                    </MenuItem>
                    {categoryOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box mb={3}>
                <SoftTypography variant="button" fontWeight="medium" mb={1}>
                  Issuance Mode
                </SoftTypography>
                <FormControl fullWidth sx={selectSx}>
                  <Select
                    name="issuance_mode"
                    value={formData.issuance_mode}
                    onChange={handleChange}
                  >
                    {issuanceModeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {isAutoMode && (
                <>
                  <SoftBox mb={3} p={2} sx={{ border: "1px solid #eef2f6", borderRadius: "12px" }}>
                    <SoftTypography variant="button" fontWeight="bold" mb={2} display="block">
                      Auto Trigger
                    </SoftTypography>

                    {!formData.category && (
                      <SoftTypography variant="caption" color="text" display="block" mb={2}>
                        Select a category above to configure the trigger.
                      </SoftTypography>
                    )}

                    {formData.category === "level" && (
                      <Box mb={3}>
                        <SoftTypography variant="caption" fontWeight="medium" mb={1} display="block">
                          Target Level
                        </SoftTypography>
                        <TextField
                          fullWidth
                          type="number"
                          name="trigger_level"
                          value={formData.trigger_level}
                          onChange={handleChange}
                          placeholder="e.g. 10"
                          sx={fieldSx}
                        />
                      </Box>
                    )}

                    {formData.category === "badge" && (
                      <>
                        <Box mb={3}>
                          <SoftTypography variant="caption" fontWeight="medium" mb={1} display="block">
                            Badge Trigger Type
                          </SoftTypography>
                          <FormControl fullWidth sx={selectSx}>
                            <Select
                              name="badge_trigger_type"
                              value={formData.badge_trigger_type}
                              onChange={handleChange}
                            >
                              <MenuItem value="badge_key">When user earns a specific badge</MenuItem>
                              <MenuItem value="rarity_tier">When user earns any badge of rarity tier</MenuItem>
                            </Select>
                          </FormControl>
                        </Box>

                        {formData.badge_trigger_type === "badge_key" ? (
                          <Box mb={3}>
                            <SoftTypography variant="caption" fontWeight="medium" mb={1} display="block">
                              Select Badge
                            </SoftTypography>
                            <FormControl fullWidth sx={selectSx}>
                              <Select
                                value={formData.selected_badge_id}
                                onChange={(e) => handleBadgeSelect(e.target.value)}
                                displayEmpty
                                disabled={badgesLoading}
                              >
                                <MenuItem value="" disabled>
                                  {badgesLoading ? "Loading badges..." : "Choose a badge..."}
                                </MenuItem>
                                {badgeOptions.map((badge) => (
                                  <MenuItem key={badge._id} value={badge._id}>
                                    {formatBadgeLabel(badge)}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            {formData.trigger_badge_key && (
                              <SoftTypography variant="caption" color="text" display="block" mt={1}>
                                Trigger key: {formData.trigger_badge_key}
                              </SoftTypography>
                            )}
                            {!badgesLoading && badgeOptions.length === 0 && (
                              <SoftTypography variant="caption" color="text" display="block" mt={1}>
                                No badges found. Add badges from the Badges page first.
                              </SoftTypography>
                            )}
                          </Box>
                        ) : (
                          <Box mb={3}>
                            <SoftTypography variant="caption" fontWeight="medium" mb={1} display="block">
                              Rarity Tier
                            </SoftTypography>
                            <FormControl fullWidth sx={selectSx}>
                              <Select
                                name="trigger_rarity_tier"
                                value={formData.trigger_rarity_tier}
                                onChange={handleChange}
                              >
                                {rarityTierOptions.map((tier) => (
                                  <MenuItem key={tier} value={tier}>
                                    {tier}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                        )}
                      </>
                    )}

                    {formData.category === "leaderboard" && (
                      <Box mb={3}>
                        <SoftTypography variant="caption" fontWeight="medium" mb={1} display="block">
                          Placement
                        </SoftTypography>
                        <TextField
                          fullWidth
                          type="number"
                          name="trigger_placement"
                          value={formData.trigger_placement}
                          onChange={handleChange}
                          placeholder="e.g. 1 for first place"
                          sx={fieldSx}
                        />
                        <SoftTypography variant="caption" color="text" display="block" mt={1}>
                          Auto-issues when admin freezes the leaderboard season.
                        </SoftTypography>
                      </Box>
                    )}

                    <Box mb={3}>
                      <SoftTypography variant="caption" fontWeight="medium" mb={1} display="block">
                        Expiry (days after auto issue)
                      </SoftTypography>
                      <TextField
                        fullWidth
                        type="number"
                        name="expiry_days"
                        value={formData.expiry_days}
                        onChange={handleChange}
                        placeholder="90"
                        sx={fieldSx}
                      />
                    </Box>

                    <Box mb={1}>
                      <SoftTypography variant="caption" fontWeight="medium" mb={1} display="block">
                        Auto Issue Benefits
                      </SoftTypography>
                      {formData.benefits.map((benefit, index) => (
                        <Box key={`benefit-${index}`} display="flex" alignItems="center" gap={1} mb={1.5}>
                          <TextField
                            fullWidth
                            value={benefit}
                            onChange={(e) => handleBenefitChange(index, e.target.value)}
                            placeholder="e.g. Free shipping on next order"
                            sx={fieldSx}
                          />
                          {formData.benefits.length > 1 && (
                            <IconButton
                              onClick={() => handleRemoveBenefit(index)}
                              sx={removeIconButtonSx}
                              size="small"
                            >
                              <RemoveIcon fontSize="small" />
                            </IconButton>
                          )}
                          {index === formData.benefits.length - 1 && (
                            <IconButton onClick={handleAddBenefit} sx={iconButtonSx} size="small">
                              <AddIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </SoftBox>
                </>
              )}

              {isEditMode && (
                <Box mb={4}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_enabled}
                        onChange={handleEnabledChange}
                        color="info"
                      />
                    }
                    label={
                      <SoftTypography variant="button" fontWeight="medium">
                        Enabled
                      </SoftTypography>
                    }
                  />
                </Box>
              )}

              <Box display="flex" justifyContent="flex-end">
                <SoftButton
                  type="submit"
                  variant="gradient"
                  color="info"
                  disabled={isSubmitting}
                >
                  {isSubmitting && (
                    <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                  )}
                  {isSubmitting
                    ? isEditMode
                      ? "Updating..."
                      : "Saving..."
                    : isEditMode
                    ? "Update Reward"
                    : "Create Reward"}
                </SoftButton>
              </Box>
            </form>
          </SoftBox>
        </Card>
      </SoftBox>
    </SoftBox>
  );
}
